import { Box } from '@mui/material';
import { GridActionsCellItemProps, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { H1, TableAdvancedMetaColumn, usersColumnVisibilityModel, UsersTable } from 'src/components';
import config from 'src/config';
import { setSharedUserStatus, setUserStatus } from 'src/redux/user/actions';
import { PermissionEnum } from 'src/shared/enums/permission.enum';
import { UserStatus } from 'src/shared/enums/userStatus.enum';
import { UserType } from 'src/types';

import emptyUsers from 'src/assets/emptyUsers.svg';
import { useQueryParam } from 'src/hooks';
import { selectSetSharedUserStatusSuccess, selectSetUserStatusSuccess } from 'src/redux/user/selectors';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setParams, translationExists } from 'src/utils';
import { PATHS } from 'src/navigation';
import { SharedRoleSlug } from 'src/shared/enums/roleSlug.enum';
import { selectCurrTenantId } from 'src/redux/tenants/selectors';
import { loadTenantUsersList } from 'src/redux/tenants/actions';
import StyledActionItem from 'src/components/Table/TableComponents/StyledActionItem';

type RegisteredUsersTabProps = {
  users: UserType[];
};

const RegisteredUsersTab = ({ users }: RegisteredUsersTabProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userId, setUserId] = useQueryParam('id', '');
  const setUserStatusSuccess = useSelector(selectSetUserStatusSuccess);
  const setSharedUserStatusSuccess = useSelector(selectSetSharedUserStatusSuccess);
  const tenantId = useSelector(selectCurrTenantId);

  const canViewUserProfile = config.can([PermissionEnum.VIEW_FULL_USER_PROFILE]) || config.can([PermissionEnum.VIEW_FIELD_USER_PROFILE]);
  const canEditAnyStatus = [PermissionEnum.APPROVE_USER, PermissionEnum.ACTIVATE_USER, PermissionEnum.BLOCK_USER, PermissionEnum.UNBLOCK_USER].some(
    (permission) => config.can(permission),
  );

  const onSetNewStatus = (user: UserType, status: UserStatus) => {
    const userRoleSlug = user.roles?.[0]?.slug ?? '';
    if (Object.values(SharedRoleSlug).includes(userRoleSlug as SharedRoleSlug)) {
      return dispatch(setSharedUserStatus.init({ tenantId, uuid: user.uuid, status }));
    }
    return dispatch(setUserStatus.init({ uuid: user.uuid, status }));
  };

  const navigateToUserProfile = (id: string, firstName: string, lastName: string) => {
    setUserId(id);
    navigate(`${setParams(PATHS.USER_PROFILE_PAGE, { uuid: id })}?name=${`${firstName} ${lastName}`}`);
  };

  const clearUserId = () => {
    setUserId('');
  };

  const goTo = (params: GridRenderCellParams) => {
    navigateToUserProfile(params.row.uuid, params.row.firstName, params.row.lastName);
  };

  const columnsVisibility = {
    ...usersColumnVisibilityModel,
    firstName: true,
    lastName: true,
    email: true,
    registrationDate: true,
    clearStatus: true,
    userRoles: true,
    verificationStatus: true,
    status: true,
  };

  const hiddenFromToggleFields = ['favorite'];

  const getTogglableColumns = (columns: any) => {
    return columns.filter((column: any) => !hiddenFromToggleFields.includes(column.field)).map((column: any) => column.field);
  };

  const getActions = (params: GridRowParams<any>): React.ReactElement<GridActionsCellItemProps>[] => {
    const actions: { [key: number]: any } = {
      [UserStatus.DEACTIVATED]: () => [
        config.can(PermissionEnum.APPROVE_USER) ? (
          <StyledActionItem key="approveCell" label="Approve" onClick={() => onSetNewStatus(params.row, UserStatus.APPROVED)} showInMenu />
        ) : null,
        config.can(PermissionEnum.BLOCK_USER) ? (
          <StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />
        ) : null,
      ],
      [UserStatus.APPROVED]: () => [
        config.can(PermissionEnum.ACTIVATE_USER) ? (
          <StyledActionItem key="activateCell" label="Activate" onClick={() => onSetNewStatus(params.row, UserStatus.ACTIVE)} showInMenu />
        ) : null,
        config.can(PermissionEnum.BLOCK_USER) ? (
          <StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />
        ) : null,
      ],
      [UserStatus.ACTIVE]: () => [
        config.can(PermissionEnum.BLOCK_USER) ? (
          <StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />
        ) : null,
      ],
      [UserStatus.BLOCKED]: () => [
        config.can(PermissionEnum.UNBLOCK_USER) ? (
          <StyledActionItem key="unblockCell" label="Unblock" onClick={() => onSetNewStatus(params.row, UserStatus.DEACTIVATED)} showInMenu />
        ) : null,
      ],
    };

    return actions[params.row.status]().filter((item: any) => item !== null) ?? [];
  };

  useEffect(() => {
    if (setUserStatusSuccess || setSharedUserStatusSuccess) {
      dispatch(loadTenantUsersList.init({ tenantId }));
    }
  }, [setUserStatusSuccess, setSharedUserStatusSuccess]);

  const metaColumns: TableAdvancedMetaColumn[] = [
    {
      index: 9,
      column: {
        field: 'localDetails',
        headerName: 'Classifications',
        width: 240,
        renderCell: (params) => {
          const localUnion = params.row.unionDetails?.localUnion ?? '';
          const unionClassification = params.row.unionDetails?.unionClassification ?? '';
          const keyToCheck = `union_classifications.${localUnion}.${unionClassification}`;
          const translationKey = translationExists(keyToCheck) ? keyToCheck : `union_classifications.GENERIC.${unionClassification}`;
          return unionClassification ? t(translationKey) : '';
        },
      },
    },
  ];

  return (
    <Box>
      {users?.length ? (
        <Box component="main" sx={{ height: 'calc(100vh - 150px)', pt: 4 }}>
          <UsersTable
            statePersistId="user_management_table"
            users={users}
            columnVisibilityModel={columnsVisibility}
            getActions={canEditAnyStatus ? getActions : undefined}
            toolbarProps={{ options: 'mini' }}
            goTo={canViewUserProfile ? goTo : undefined}
            onCellClick={(params) =>
              params.field === 'firstName' || params.field === 'lastName'
                ? navigateToUserProfile(params.row.uuid, params.row.firstName, params.row.lastName)
                : undefined
            }
            slotProps={{ columnsPanel: { getTogglableColumns } }}
            disableVirtualization
            usePaginaionParams
            idToScroll={userId}
            onScroll={clearUserId}
            metaColumns={metaColumns}
          />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ ml: 1 }}>
            <img src={emptyUsers} alt="Empty list" />
          </Box>
          <H1 sx={{ mb: 3 }}>{t('usersPage.noUsers')}</H1>
        </Box>
      )}
    </Box>
  );
};

export default RegisteredUsersTab;
